import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useGTM } from "hooks/useGTM";
import { gamesApi } from "redux/game/api";
import { GAME_MODE } from "types/Games";
import { getLocalStorageItem } from "utils/localStorage";
import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { setCurrentGame, setCurrentGameAlias } from "redux/game/slice";
import { usePlatform } from "./usePlatform";

export const useLoadGame = (): [
  (mode: GAME_MODE, gameAlias: string) => Promise<void>,
  { isLoading: boolean; isError: boolean },
] => {
  const dispatch = useDispatch();
  const platform = usePlatform();
  const GTM = useGTM();
  const [searchParams] = useSearchParams();

  const [
    initByAlias,
    { isLoading: isLoadingInitByAlias, isError: isInitErrorByAlias },
  ] = gamesApi.useInitByAliasMutation();
  const [
    initDemoByAlias,
    { isLoading: isLoadingInitDemoByAlias, isError: isErrorInitDemoByAlias },
  ] = gamesApi.useInitDemoByAliasMutation();

  const lang = getLocalStorageItem(STORAGE_LANGUAGE_NAME) || "EN";

  async function loadGame(mode: GAME_MODE, gameAlias: string) {
    if (isLoadingInitByAlias || isLoadingInitDemoByAlias) {
      return;
    }
    const tournamentParam = searchParams.get("tournament");
    const [tournamentId, tournamentName] = tournamentParam
      ? tournamentParam.split(":")
      : [];
    // eslint-disable-next-line sonarjs/no-collapsible-if
    const params = {
      returnUrl: window.location.origin,
      lang,
      alias: gameAlias,
      platform,
      mode: mode === GAME_MODE.DEMO ? GAME_MODE.DEMO : GAME_MODE.REAL,
    };
    const response =
      mode === GAME_MODE.DEMO
        ? await initDemoByAlias(params)
        : await initByAlias(params);

    // eslint-disable-next-line sonarjs/no-collapsible-if
    if (tournamentParam && "data" in response) {
      GTM.clickTournamentGamePlay(
        response.data.game,
        Number(tournamentId),
        tournamentName,
      );

      if ("data" in response) {
        if (mode === GAME_MODE.DEMO) {
          GTM.clickStartDemoGame(response.data.game);
        } else {
          GTM.clickStartRealGame(response.data.game);
        }
      }
    }
    if ("data" in response) {
      dispatch(setCurrentGame(response?.data?.game));
    }
    dispatch(setCurrentGameAlias(gameAlias));
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return [
    loadGame,
    {
      isLoading: isLoadingInitByAlias || isLoadingInitDemoByAlias,
      isError: isInitErrorByAlias || isErrorInitDemoByAlias,
    },
  ];
};
